import React, { useMemo } from 'react';
import ProgressBar from './ProgressBar.jsx';
import Badge from '../ui/Badge.jsx';
import Button from '../ui/Button.jsx';

function isAllNotConcerned(category, answers) {
  if (!answers || !category?.questions?.length) return false;
  return category.questions.every((q) => {
    const a = answers[q.question_uuid];
    return a && a.share_score === 0 && a.burden_score === 0 && a.thought_score === -1;
  });
}

function progressBarClass(percent, allNotConcerned) {
  if (allNotConcerned) return 'bg-ptcm-sand';
  if (percent >= 67) return 'bg-ptcm-success';
  if (percent >= 34) return 'bg-ptcm-primary';
  return 'bg-ptcm-warning';
}

export default function CategoryRow({
  category,
  progress,
  answers,
  isMarking,
  onOpen,
  onMarkNotRelevant,
  onResetCategory,
  icon: Icon
}) {
  const total = progress?.totalQuestionsInCategory ?? category.questions.length;
  const answered = progress?.answeredQuestionsInCategory ?? 0;
  const percent = progress?.percent ?? 0;
  const isDone = percent === 100;
  const isEmpty = answered === 0 && !isDone;

  const allNotConcerned = useMemo(() => isAllNotConcerned(category, answers), [category, answers]);
  const isNotConcernedDone = isDone && allNotConcerned;
  const barClassName = progressBarClass(percent, allNotConcerned);

  const ctaLabel = isDone ? 'Voir' : (answered > 0 ? 'Continuer' : 'Commencer');
  // UX rule: "Non concerné" only when the user hasn't started this category.
  const canMarkNotRelevant = Boolean(answers) && isEmpty;

  const questionsText = `${total} question${total > 1 ? 's' : ''}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className="group relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 sm:p-5 hover:bg-ptcm-panel/45 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
    >
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-10 h-10 rounded-2xl bg-ptcm-primary-soft dark:bg-blue-900/20 flex items-center justify-center text-ptcm-primary dark:text-blue-400 shrink-0">
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-ptcm-ink dark:text-gray-100 group-hover:text-ptcm-primary dark:group-hover:text-blue-400 transition-colors">
                {category.label}
              </h3>
              {isDone && (
                <Badge variant={allNotConcerned ? 'outline' : 'success'} className="shrink-0 scale-90 origin-left">
                  {allNotConcerned ? 'Non concerné' : 'Terminé'}
                </Badge>
              )}
            </div>
            <div className="text-sm text-ptcm-muted dark:text-gray-400 mt-0.5">
                {!isEmpty && !isNotConcernedDone ? (
                    <div className="flex items-center gap-3">
                    <div className="w-24 sm:w-32 hidden sm:block">
                        <ProgressBar percent={percent} barClassName={barClassName} heightClassName="h-1.5" />
                    </div>
                    <span className="text-xs font-medium">{percent}%</span>
                    <span className="text-xs text-ptcm-muted/70">• {answered}/{total}</span>
                    </div>
                ) : (
                    <span className="text-xs">{isNotConcernedDone ? 'Non concerné' : questionsText}</span>
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {isNotConcernedDone ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={isMarking}
            onClick={(e) => {
              e.stopPropagation();
              onResetCategory?.();
            }}
            className="text-xs h-8 px-2"
          >
            {isMarking ? 'Annulation…' : 'Annuler'}
          </Button>
        ) : isEmpty ? (
          <div className="flex items-center gap-2">
            {canMarkNotRelevant ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={isMarking}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkNotRelevant();
                }}
                className="text-xs h-8 px-2"
              >
                Non concerné
              </Button>
            ) : null}
            <Button
              size="sm"
              disabled={isMarking}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              Commencer
            </Button>
          </div>
        ) : (
          <Button
            variant={isDone ? 'ghost' : 'default'}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            {ctaLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
